import { z } from 'zod'

import { Name } from '../../_kernel/Name'
import { FullName } from '../../_kernel/FullName'
import { Slug } from '../../_kernel/Slug'
import { Count } from '../../_kernel/Count'
import { Description } from '../../_kernel/Description'
import { Model } from '../../_kernel/architecture'
import { DomainError } from '../../_kernel/DomainError'
import { ErrorsCode } from '../../_kernel/ErrorsCode'
import type { GETTopDestinationsByBannerIdResponse } from '../infrastructure/HTTPMarketingRepository/schemas/banners/:id/destinations'
import { Path } from '../../_kernel/Path'

export const TopDestinationValidation = z.object({
  name: z.instanceof(Name, { message: 'Name is required' }),
  fullName: z.instanceof(FullName, { message: 'FullName is required' }),
  slug: z.instanceof(Slug, { message: 'Slug is required' }),
  accommodationCount: z.instanceof(Count, { message: 'Count is required' }),
  description: z.instanceof(Description, { message: 'Description is required' }),
  imageWide: z.instanceof(Path, { message: 'Image is required' }).optional(),
  imagePortrait: z.instanceof(Path, { message: 'Image is required' }).optional(),
})

export class TopDestination extends Model {
  static create({
    name,
    fullName,
    slug,
    accommodationCount,
    description,
    imageWide,
    imagePortrait,
  }: z.infer<typeof TopDestinationValidation>) {
    TopDestinationValidation.parse({
      name,
      fullName,
      slug,
      accommodationCount,
      description,
      imageWide,
      imagePortrait,
    })

    if (!imageWide && !imagePortrait) {
      throw DomainError.withCodes(ErrorsCode.TOP_DESTINATION_AT_LEAST_ONE_IMAGE)
    }

    return new TopDestination(
      name,
      fullName,
      slug,
      accommodationCount,
      description,
      imageWide,
      imagePortrait
    )
  }

  static fromAPI({
    json,
  }: {
    json: Omit<GETTopDestinationsByBannerIdResponse['destinations'][number], 'images'> &
      Partial<Pick<GETTopDestinationsByBannerIdResponse['destinations'][number], 'images'>>
  }) {
    return TopDestination.create({
      name: Name.create({ value: json.name }),
      fullName: FullName.create({ value: json.full_name }),
      slug: Slug.create({ value: json.slug }),
      accommodationCount: Count.create({ value: json.properties_count }),
      description: Description.create({ value: json.description }),
      ...(json.images?.wide && { imageWide: Path.create({ value: json.images.wide }) }),
      ...(json.images?.portrait && { imagePortrait: Path.create({ value: json.images.portrait }) }),
    })
  }

  static fromJSON(json: ReturnType<TopDestination['toJSON']>) {
    return TopDestination.create({
      name: Name.create({ value: json.name }),
      fullName: FullName.create({ value: json.fullName }),
      slug: Slug.create({ value: json.slug }),
      accommodationCount: Count.create({ value: json.accommodationCount }),
      description: Description.create({ value: json.description }),
      ...(json.images?.wide && { imageWide: Path.create({ value: json.images.wide }) }),
      ...(json.images?.portrait && { imagePortrait: Path.create({ value: json.images.portrait }) }),
    })
  }

  constructor(
    private readonly _name: Name,
    private readonly _fullName: FullName,
    private readonly _slug: Slug,
    private readonly _accommodationCount: Count,
    private readonly _description: Description,
    private readonly _imageWide?: Path,
    private readonly _imagePortrait?: Path
  ) {
    super()
  }

  get name() { return this._name.value } // prettier-ignore
  get fullName() { return this._fullName.value } // prettier-ignore
  get slug() { return this._slug.value } // prettier-ignore
  get accommodationCount() { return this._accommodationCount.value } // prettier-ignore
  get description() { return this._description.value } // prettier-ignore
  get imageWide() { return this._imageWide?.value } // prettier-ignore
  get imagePortrait() { return this._imagePortrait?.value } // prettier-ignore

  imagePath({ mode }: { mode: 'wide' | 'portrait' }): string {
    const image = mode === 'wide' ? this.imageWide : this.imagePortrait

    if (!image) {
      throw DomainError.withCodes(ErrorsCode.TOP_DESTINATION_IMAGE_NOT_FOUND)
    }

    return image
  }

  toJSON() {
    return {
      name: this.name,
      fullName: this.fullName,
      slug: this.slug,
      accommodationCount: this.accommodationCount,
      description: this.description,
      images: {
        wide: this.imageWide ?? null,
        portrait: this.imagePortrait ?? null,
      },
    }
  }
}
